import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { InfoBlock, ListTable, orDash } from './DetailParts';
import { AGE_LIMIT_YEARS, VERDICT_COLORS, VERDICT_LABELS } from './systemInfoSummary';
import { yesNo } from './updateSummary';

const asList = (v) => (Array.isArray(v) ? v : []);
const SEVERITY_ORDER = ['hard', 'strong', 'soft'];

// One machine's system information: the hardware and system details, how old it is, and whether it should be replaced
// and why. `data` is the whole result of the system info script (Get-SystemInfo). A result written before the age and
// replacement parts were added has only the system details, and the tab says so.
function SystemInfoTab({ data }) {
  const r = data || {};
  const age = r.DeviceAge || null;
  const assessment = r.ReplacementAssessment || null;
  const metrics = assessment?.metrics || {};
  const findings = asList(assessment?.findings)
    .slice()
    .sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity));
  const reasons = asList(assessment?.verdictReasons);
  const notRun = asList(r.ChecksNotRun);
  const ageYears = age?.estimatedAgeYears;
  const older = typeof ageYears === 'number' && ageYears > AGE_LIMIT_YEARS;

  return (
    <Stack spacing={2}>
      {!assessment && (
        <Alert severity="info">
          This machine has not reported its age or replacement assessment yet. They appear after the system info script
          next runs in its current version.
        </Alert>
      )}

      {assessment && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <Chip
              label={VERDICT_LABELS[assessment.verdict] || orDash(assessment.verdict)}
              color={VERDICT_COLORS[assessment.verdict] || 'default'}
            />
            {older && <Chip variant="outlined" color="info" label={`Older than ${AGE_LIMIT_YEARS} years`} />}
            {typeof ageYears === 'number' && (
              <Typography variant="body2" color="text.secondary">
                About {ageYears} years old{age?.ageSource ? ` (from ${age.ageSource === 'serial' ? 'the serial number' : 'the first Windows install'})` : ''}
              </Typography>
            )}
          </Stack>
          {reasons.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {reasons.map((reason, i) => <Typography key={i} variant="body2">- {String(reason)}</Typography>)}
            </Box>
          )}
        </Paper>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <InfoBlock title="Device" rows={[
            ['Manufacturer', r.Manufacturer], ['Model', r.Model], ['Serial number', r.SerialNumber],
            ['Type', assessment?.chassis], ['Processor', r.Processor], ['Cores', r.TotalPhysicalCores],
            ['Memory installed (GB)', r.MemoryInstalledGB], ['Memory usable (GB)', r.MemoryUsableGB],
            ['Motherboard', r.Motherboard], ['BIOS', r.BiosName], ['BIOS version', r.BiosVersion],
            ['BIOS date', r.BiosReleaseDate], ['Display adapters', asList(r.DisplayAdapters).join(', ')],
          ]} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <InfoBlock title="Operating system" rows={[
            ['Name', r.OperatingSystem], ['Architecture', r.Architecture], ['Windows version', r.WindowsVersion],
            ['Display version', r.WindowsDisplayVersion], ['Installation type', r.WindowsInstallationType],
            ['Last user', r.LastUser], ['Domain', r.Domain], ['Part of a domain', yesNo(r.PartOfDomain)],
            ['Antivirus', r.AntivirusProduct], ['Firewall on', yesNo(r.WindowsFirewallEnabled)],
            ['.NET Framework', r.DotNetVersion], ['Services', r.ServiceCount],
          ]} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <InfoBlock title="Age" rows={age ? [
            ['Estimated age (years)', age.estimatedAgeYears], ['Estimated build date', age.estimatedBuildDate],
            ['Based on', age.ageSource], ['First Windows install', age.firstInstallDate],
            ['Serial build date', age.serialBuildDate], ['Install dates', asList(age.installDates).join(', ')],
            ['Drive power-on hours', age.powerOnHours],
            ['Replacement age limit (years)', assessment?.dates?.ageLimitYears],
            ['Replacement due', assessment?.dates?.replacementDueDate],
          ] : [['Estimated age', 'Not reported yet']]} />
        </Grid>
      </Grid>

      {assessment && (
        <>
          <ListTable
            title="Findings"
            rows={findings}
            columns={[
              { key: 'severity', label: 'Severity' }, { key: 'area', label: 'Area' }, { key: 'issue', label: 'Issue' },
            ]}
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <InfoBlock title="Windows 11" rows={[
                ['On Windows 11', yesNo(metrics.windows11?.onWindows11)],
                ['Can run Windows 11', yesNo(metrics.windows11?.eligible)],
                ['Blocked by', asList(metrics.windows11?.reasons).join(', ')],
                ['Checked by', metrics.windows11?.source],
              ]} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <InfoBlock title="Stability and boot" rows={[
                ['Days looked at', metrics.stability?.days],
                ['Unexpected shutdowns', metrics.stability?.unexpectedShutdowns],
                ['Blue screens', metrics.stability?.blueScreens],
                ['Reliability index (of 10)', metrics.stability?.reliabilityIndex],
                ['Boot time, median (s)', metrics.boot?.medianSeconds],
                ['Boots measured', metrics.boot?.samples],
              ]} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <InfoBlock title="Windows drive and battery" rows={[
                ['Drive size (GB)', metrics.systemDrive?.sizeGB], ['Free (GB)', metrics.systemDrive?.freeGB],
                ['Free (%)', metrics.systemDrive?.freePercent],
                ['Battery health (%)', metrics.battery?.healthPercent],
                ['Battery design (mWh)', metrics.battery?.designMWh],
                ['Battery full charge (mWh)', metrics.battery?.fullChargeMWh],
              ]} />
            </Grid>
          </Grid>

          <ListTable
            title="Drives"
            rows={asList(metrics.drives).map(d => ({ ...d, isSystem: yesNo(d.isSystem) }))}
            columns={[
              { key: 'number', label: 'Number' }, { key: 'model', label: 'Model' }, { key: 'mediaType', label: 'Type' },
              { key: 'busType', label: 'Bus' }, { key: 'isSystem', label: 'Windows drive' }, { key: 'health', label: 'Health' },
              { key: 'wearPercent', label: 'Wear (%)' }, { key: 'readErrors', label: 'Read errors' },
            ]}
          />
        </>
      )}

      {notRun.length > 0 && (
        <ListTable
          title="Could not be read"
          rows={notRun}
          columns={[{ key: 'check', label: 'Check' }, { key: 'reason', label: 'Reason' }]}
        />
      )}

      <Accordion variant="outlined" disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2">Raw result (JSON)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {JSON.stringify(r, null, 2)}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}

export default SystemInfoTab;
